/**
 * Mon agenda : disponibilités déclarées et rendez-vous (section 4.4).
 *
 * Les disponibilités se déclarent par blocs d'une demi-heure sur une grille —
 * cocher des cases au pouce est le seul geste raisonnable debout ; un
 * sélecteur d'heure ne l'est pas. La grille est envoyée en remplacement
 * intégral : trivial à rejouer quand le réseau revient.
 */
import { useEffect, useMemo, useState } from 'react';
import { appelerApi, envoyer } from '../reseau.ts';
import { useSession } from '../etat.tsx';
import { nomAffiche } from '../composants/FicheParticipant.tsx';
import { Link } from 'react-router-dom';

type RendezVous = {
  id: string;
  debut_le: string;
  fin_le: string;
  duree_min: number;
  lieu: string | null;
  message: string | null;
  statut: string;
  initie_par_moi: boolean;
  autre_participant_id: string;
  fiche: { prenom: string | null; nom: string | null; entreprise: string | null; fonction: string | null } | null;
};

type Disponibilite = { id: string; debut_le: string; fin_le: string };

const heure = (iso: string) =>
  new Date(iso).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' });

const LIBELLES_STATUT: Record<string, string> = {
  propose: 'Proposé',
  confirme: 'Confirmé',
  refuse: 'Décliné',
  honore: 'Honoré',
  manque: 'Manqué',
};

export function Agenda() {
  const { moi } = useSession();
  const [rendezVous, setRendezVous] = useState<RendezVous[]>([]);
  const [blocsCoches, setBlocsCoches] = useState<Set<number>>(new Set());
  // Fenêtres ajoutées à la main, hors de la grille de l'événement (avant, après,
  // ou un autre jour) : elles vivent à côté des blocs et sont enregistrées avec.
  const [horsPlage, setHorsPlage] = useState<{ debut: string; fin: string }[]>([]);
  const [nouveauDebut, setNouveauDebut] = useState('');
  const [nouveauFin, setNouveauFin] = useState('');
  const [dispoModifiee, setDispoModifiee] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Grille : blocs de 30 min entre le début et la fin de l'événement.
  const blocs = useMemo(() => {
    if (!moi) return [];
    const debut = new Date(moi.debut_le).getTime();
    const fin = new Date(moi.fin_le).getTime();
    const sortie: { indice: number; debut: Date }[] = [];
    for (let t = debut, i = 0; t < fin; t += 30 * 60_000, i++) {
      sortie.push({ indice: i, debut: new Date(t) });
    }
    return sortie;
  }, [moi]);

  const chargerAgenda = async () => {
    const r = await appelerApi<{ rendez_vous: RendezVous[] }>('/api/rendez-vous/');
    if (r.donnees) setRendezVous(r.donnees.rendez_vous);
  };

  useEffect(() => {
    void chargerAgenda();
    void appelerApi<{ disponibilites: Disponibilite[] }>('/api/rendez-vous/disponibilites').then(
      (r) => {
        if (!r.donnees || !moi) return;
        const debutEvenement = new Date(moi.debut_le).getTime();
        const finEvenement = new Date(moi.fin_le).getTime();
        const coches = new Set<number>();
        const dehors: { debut: string; fin: string }[] = [];
        for (const d of r.donnees.disponibilites) {
          const de = new Date(d.debut_le).getTime();
          const a = new Date(d.fin_le).getTime();
          // Une fenêtre qui déborde de l'événement ne se représente pas dans la
          // grille : on la garde telle quelle pour ne pas la perdre.
          if (de < debutEvenement || a > finEvenement) {
            dehors.push({ debut: new Date(de).toISOString(), fin: new Date(a).toISOString() });
            continue;
          }
          for (let t = de; t < a; t += 30 * 60_000) {
            coches.add(Math.round((t - debutEvenement) / (30 * 60_000)));
          }
        }
        setBlocsCoches(coches);
        setHorsPlage(dehors);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moi?.participant_id]);

  const basculerBloc = (indice: number) => {
    setBlocsCoches((coches) => {
      const suivant = new Set(coches);
      if (suivant.has(indice)) suivant.delete(indice);
      else suivant.add(indice);
      return suivant;
    });
    setDispoModifiee(true);
  };

  const toutCocher = (tout: boolean) => {
    setBlocsCoches(tout ? new Set(blocs.filter((b) => !estPasse(b.debut)).map((b) => b.indice)) : new Set());
    setDispoModifiee(true);
  };

  const ajouterHorsPlage = () => {
    if (!nouveauDebut || !nouveauFin) return;
    const debut = new Date(nouveauDebut);
    const fin = new Date(nouveauFin);
    if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime()) || fin <= debut) {
      setMessage('Le créneau doit finir après son début.');
      return;
    }
    setHorsPlage((liste) => [...liste, { debut: debut.toISOString(), fin: fin.toISOString() }]);
    setNouveauDebut('');
    setNouveauFin('');
    setDispoModifiee(true);
    setMessage(null);
  };

  const enregistrerDisponibilites = async () => {
    if (!moi) return;
    setEnregistrement(true);
    const debutEvenement = new Date(moi.debut_le).getTime();
    // Blocs contigus → fenêtres.
    const indices = [...blocsCoches].sort((a, b) => a - b);
    const fenetres: { debut: string; fin: string }[] = [];
    for (const indice of indices) {
      const debut = new Date(debutEvenement + indice * 30 * 60_000);
      const fin = new Date(debut.getTime() + 30 * 60_000);
      const derniere = fenetres[fenetres.length - 1];
      if (derniere && derniere.fin === debut.toISOString()) derniere.fin = fin.toISOString();
      else fenetres.push({ debut: debut.toISOString(), fin: fin.toISOString() });
    }
    const r = await envoyer('/api/rendez-vous/disponibilites', { fenetres: [...fenetres, ...horsPlage] }, 'PUT');
    setEnregistrement(false);
    setDispoModifiee(false);
    setMessage(r.erreur ?? 'Disponibilités enregistrées.');
  };

  const repondre = async (id: string, accepte: boolean) => {
    const r = await envoyer(`/api/rendez-vous/${id}/repondre`, { accepte });
    if (r.erreur) setMessage(r.erreur);
    void chargerAgenda();
  };

  const annuler = async (id: string) => {
    await envoyer(`/api/rendez-vous/${id}/annuler`, {});
    void chargerAgenda();
  };

  const aVenir = rendezVous.filter((r) => r.statut !== 'refuse');
  // Un créneau déjà passé ne se coche pas : il ne servirait à personne.
  const estPasse = (d: Date) => d.getTime() < Date.now();

  return (
    <div className="agenda">
      <h1>Mon agenda</h1>

      <AConfirmer onChange={() => void chargerAgenda()} />

      <section className="agenda__rdv" aria-labelledby="titre-rdv">
        <h2 id="titre-rdv" className="visuellement-masque">Rendez-vous</h2>
        {aVenir.length === 0 ? (
          <p className="agenda__vide">
            Aucun rendez-vous pour l'instant. Parcourez les <Link to="/participants">participants</Link>{' '}
            pour en proposer un.
          </p>
        ) : (
          <ul className="agenda__liste">
            {aVenir.map((r) => (
              <li key={r.id} className={`carte rdv rdv--${r.statut}`}>
                <div className="rdv__heure">
                  <span className="rdv__debut">{heure(r.debut_le)}</span>
                  <span className="rdv__duree">{r.duree_min} min</span>
                </div>
                <div className="rdv__corps">
                  <Link to={`/participants/${r.autre_participant_id}`} className="rdv__nom">
                    {r.fiche ? nomAffiche({ ...r.fiche }) : 'Participant'}
                  </Link>
                  <p className="rdv__detail">
                    {[r.fiche?.fonction, r.fiche?.entreprise].filter(Boolean).join(' · ')}
                  </p>
                  {r.lieu && <p className="rdv__lieu">{r.lieu}</p>}
                  <span className={`etiquette rdv__statut rdv__statut--${r.statut}`}>
                    {LIBELLES_STATUT[r.statut] ?? r.statut}
                  </span>
                </div>
                <div className="rdv__actions">
                  {r.statut === 'propose' && !r.initie_par_moi && (
                    <>
                      <button className="bouton" onClick={() => void repondre(r.id, true)}>
                        Accepter
                      </button>
                      <button
                        className="bouton bouton--secondaire"
                        onClick={() => void repondre(r.id, false)}
                      >
                        Décliner
                      </button>
                    </>
                  )}
                  {(r.statut === 'confirme' || (r.statut === 'propose' && r.initie_par_moi)) && (
                    <button className="bouton bouton--secondaire" onClick={() => void annuler(r.id)}>
                      Annuler
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="carte agenda__disponibilites" aria-labelledby="titre-dispo">
        <h2 id="titre-dispo">Mes disponibilités</h2>
        <p className="agenda__aide">
          Cochez les moments où l'on peut vous proposer un rendez-vous. Sans rien cocher, on peut
          toujours vous proposer un Speed Match de 5 min.
        </p>

        <div className="agenda__actions-dispo">
          <button className="bouton bouton--secondaire" onClick={() => toutCocher(true)}>
            Tout cocher
          </button>
          <button className="bouton bouton--secondaire" onClick={() => toutCocher(false)}>
            Tout décocher
          </button>
          <span className="agenda__compte-dispo">
            {blocsCoches.size + horsPlage.length === 0
              ? 'Aucun créneau'
              : `${blocsCoches.size} créneau${blocsCoches.size > 1 ? 'x' : ''} de 30 min${horsPlage.length > 0 ? ` + ${horsPlage.length} hors événement` : ''}`}
          </span>
        </div>

        <div className="agenda__grille" role="group" aria-label="Blocs de disponibilité">
          {blocs.map((b) => {
            const coche = blocsCoches.has(b.indice);
            const passe = estPasse(b.debut);
            return (
              <button
                key={b.indice}
                className={`agenda__bloc${coche ? ' agenda__bloc--coche' : ''}`}
                aria-pressed={coche}
                disabled={passe}
                title={passe ? 'Déjà passé' : undefined}
                onClick={() => basculerBloc(b.indice)}
              >
                {b.debut.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}
              </button>
            );
          })}
        </div>

        <div className="agenda__ajout">
          <label>
            Ajouter un créneau — début
            <input type="datetime-local" value={nouveauDebut} onChange={(e) => setNouveauDebut(e.target.value)} />
          </label>
          <label>
            Fin
            <input type="datetime-local" value={nouveauFin} onChange={(e) => setNouveauFin(e.target.value)} />
          </label>
          <button className="bouton bouton--secondaire" onClick={ajouterHorsPlage}>
            Ajouter
          </button>
        </div>
        {horsPlage.length > 0 && (
          <div className="agenda__hors-plage">
            {horsPlage.map((f, i) => (
              <span key={`${f.debut}-${i}`} className="agenda__hors-plage-item">
                {new Date(f.debut).toLocaleString('fr-BE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' → '}
                {new Date(f.fin).toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit' })}
                <button
                  className="bouton bouton--secondaire"
                  aria-label="Retirer ce créneau"
                  onClick={() => {
                    setHorsPlage((liste) => liste.filter((_, j) => j !== i));
                    setDispoModifiee(true);
                  }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
        {dispoModifiee && (
          <button
            className="bouton"
            disabled={enregistrement}
            onClick={() => void enregistrerDisponibilites()}
          >
            {enregistrement ? 'Enregistrement…' : 'Enregistrer mes disponibilités'}
          </button>
        )}
      </section>

      {message && <p className="message-info" role="status">{message}</p>}
    </div>
  );
}


/* ————————————————— Ce que le rendez-vous a donné —————————————————
   La question se pose une fois la rencontre passée, à chacun séparément. Deux
   « oui » et deux accords de publication, et le duo passe sur le Deal Wall. */

type AConfirmerLigne = {
  id: string;
  debut_le: string;
  fin_le: string;
  duree_min: number;
  speed_match: boolean;
  autre_participant_id: string;
};

function AConfirmer({ onChange }: { onChange: () => void }) {
  const [lignes, setLignes] = useState<AConfirmerLigne[]>([]);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [objet, setObjet] = useState('');
  const [publiable, setPubliable] = useState(true);
  const [message, setMessage] = useState<string | null>(null);

  const charger = async () => {
    const r = await appelerApi<{ rendez_vous: AConfirmerLigne[] }>('/api/rendez-vous/a-confirmer');
    setLignes(r.donnees?.rendez_vous ?? []);
  };
  useEffect(() => {
    void charger();
  }, []);

  const repondre = async (id: string, concluant: boolean) => {
    const r = await envoyer<{ manque: string | null; sur_le_mur: boolean }>(`/api/rendez-vous/${id}/resultat`, {
      concluant,
      objet: concluant ? objet.trim() || null : null,
      publiable,
    });
    setObjet('');
    setOuvert(null);
    await charger();
    onChange();
    if (r.donnees?.sur_le_mur) setMessage('Vous êtes sur le Deal Wall ! +30 points.');
    else if (concluant) setMessage(`Enregistré, +30 points. Il manque ${r.donnees?.manque ?? 'la confirmation de l’autre'}.`);
    else setMessage('Merci — c’est noté.');
    setTimeout(() => setMessage(null), 6000);
  };

  if (lignes.length === 0 && !message) return null;

  return (
    <section className="carte agenda__confirmer">
      <h2>Ça a donné quoi ?</h2>
      {message && <p className="agenda__message" role="status">{message}</p>}
      {lignes.map((l) => (
        <div key={l.id} className="agenda__confirmer-ligne">
          <p>
            Votre rendez-vous de {l.duree_min} min à {heure(l.debut_le)}
            {l.speed_match ? ' (Speed Match)' : ''} —{' '}
            <Link to={`/participants/${l.autre_participant_id}`}>voir la fiche</Link>
          </p>
          {ouvert === l.id ? (
            <div className="agenda__confirmer-detail">
              <label>
                En un mot, ce que vous allez faire ensemble
                <input
                  value={objet}
                  onChange={(e) => setObjet(e.target.value)}
                  maxLength={160}
                  placeholder="Un devis pour l’agencement de leurs bureaux"
                />
              </label>
              <label className="agenda__confirmer-publiable">
                <input type="checkbox" checked={publiable} onChange={(e) => setPubliable(e.target.checked)} />
                L’afficher sur le Deal Wall (si votre interlocuteur est d’accord aussi)
              </label>
              <div className="rdv__actions">
                <button className="bouton" onClick={() => void repondre(l.id, true)}>
                  On va travailler ensemble
                </button>
                <button className="bouton bouton--secondaire" onClick={() => setOuvert(null)}>
                  Annuler
                </button>
              </div>
            </div>
          ) : (
            <div className="rdv__actions">
              <button className="bouton" onClick={() => { setOuvert(l.id); setObjet(''); setPubliable(true); }}>
                Concluant
              </button>
              <button className="bouton bouton--secondaire" onClick={() => void repondre(l.id, false)}>
                Sans suite
              </button>
            </div>
          )}
        </div>
      ))}
    </section>
  );
}
